import { type ReactNode } from 'react';
import cn from 'classnames';
import DataTable from '@creditflowresearch/uilib/Table/DataTable';
import CollectionSearch from '@creditflowresearch/uilib/CollectionSearch';
import {
  useColumnsCustomizationContext,
  type ColumnsCustomizationContextType,
} from '@/creditflowresearch/application/BondScreener/Sidebar/useColumnsCustomizationContext';
import type { IColumnCustomizationEntry } from '@/creditflowresearch/application/BondScreener/Sidebar/useColumnsCustomization';
import useHideDeselected from './useHideDeselected';
import useColumnsTable from './useColumnsTable';
import { type CustomizationPanelTabId } from './constants';
import { SearchRow } from './SearchRow';

import styles from './customizationPanel.module.scss';
import columnsStyles from './columnsTabContent.module.scss';

const getColumnLabel = (entry: IColumnCustomizationEntry): string => entry.label;

const getRowClassName = ({ isLocked, isVisible }: IColumnCustomizationEntry): string => cn({
  [columnsStyles.pinnedRow]: isLocked,
  [columnsStyles.deselectedRow]: !isLocked && !isVisible,
});

interface IColumnsTableProps {
  rows: IColumnCustomizationEntry[],
  setDraftColumns: ColumnsCustomizationContextType['setDraftColumns'],
  getDraftColumns: ColumnsCustomizationContextType['getDraftColumns'],
  getHideDeselected: () => boolean,
  onToggleHideDeselected: () => void,
  showScopeColumn?: boolean,
  emptyNode: ReactNode,
}

/**
 * Rendered inside the search, so the table hooks see the rows in the order the user sees them —
 * which is what shift-click range selection resolves against.
 */
const ColumnsTable = ({
  rows,
  setDraftColumns,
  getDraftColumns,
  getHideDeselected,
  onToggleHideDeselected,
  showScopeColumn,
  emptyNode,
}: IColumnsTableProps) => {
  const { columns, handleOrderChange } = useColumnsTable({
    rows,
    getDraftColumns,
    setDraftColumns,
    getHideDeselected,
    onToggleHideDeselected,
    showScopeColumn,
  });

  return (
    <DataTable
      dataTest="columnsTabTable"
      scrollContainerClassName={ columnsStyles.columnsTable }
      isDraggable
      orderCallback={ (oldIndex, newIndex) => handleOrderChange(oldIndex, newIndex, rows) }
      columns={ columns }
      data={ rows }
      rowKey="id"
      rowClassNameCallback={ getRowClassName }
      showEmptyStateMessageFirst
      showEmptyStateMessage={ !!emptyNode }
      emptyMessage={ emptyNode }
    />
  );
};

interface IColumnsTabContentInnerProps {
  draftColumns: IColumnCustomizationEntry[],
  setDraftColumns: ColumnsCustomizationContextType['setDraftColumns'],
  getDraftColumns: ColumnsCustomizationContextType['getDraftColumns'],
  activeTab: CustomizationPanelTabId,
  resetKey: string | number,
  showScopeColumn?: boolean,
}

const ColumnsTabContentInner = (
  {
    draftColumns, setDraftColumns, getDraftColumns, activeTab, resetKey, showScopeColumn,
  }: IColumnsTabContentInnerProps,
) => {
  const { visibleCollection, getHideDeselected, toggleHideDeselected } = useHideDeselected(draftColumns);

  return (
    <CollectionSearch
      collection={ visibleCollection }
      getText={ getColumnLabel }
      resetKey={ activeTab }
      placeholder="Filter by name"
      inputName="columnsSearch"
      dataTest="columnsSearch"
      emptyCollection={ (query: string): ReactNode => (
        <p className={ cn(styles.noResults, columnsStyles.noResults) }>
          No columns found for <span className={ styles.noResultsQuery }>&ldquo;{ query }&rdquo;</span>
        </p>
      ) }
    >
      { (input: ReactNode, filteredColumns: IColumnCustomizationEntry[], emptyNode: ReactNode): ReactNode => (
        <>
          <SearchRow input={ input } resetKey={ resetKey } />
          <ColumnsTable
            rows={ filteredColumns }
            setDraftColumns={ setDraftColumns }
            getDraftColumns={ getDraftColumns }
            getHideDeselected={ getHideDeselected }
            onToggleHideDeselected={ toggleHideDeselected }
            showScopeColumn={ showScopeColumn }
            emptyNode={ emptyNode }
          />
        </>
      ) }
    </CollectionSearch>
  );
};

interface IColumnsTabContentProps {
  activeTab: CustomizationPanelTabId,
  resetKey: string | number,
  showScopeColumn?: boolean,
}

export const ColumnsTabContent = ({ activeTab, resetKey, showScopeColumn }: IColumnsTabContentProps) => {
  const {
    draftColumns, setDraftColumns, getDraftColumns, resetKey: columnsResetKey,
  } = useColumnsCustomizationContext();

  return (
    <ColumnsTabContentInner
      key={ columnsResetKey }
      draftColumns={ draftColumns }
      setDraftColumns={ setDraftColumns }
      getDraftColumns={ getDraftColumns }
      activeTab={ activeTab }
      resetKey={ resetKey }
      showScopeColumn={ showScopeColumn }
    />
  );
};
